/**
 * MemoryStatsCard — Displays memory extraction and search statistics
 *
 * Shows:
 * - Extraction metrics (success rate, confidence, facts/conversation)
 * - Search metrics (hybrid rate, avg results, combined score)
 * - Feedback metrics (helpful ratio, ignored count)
 * - Memory distribution (by tier, by type)
 */

import React, { useState } from 'react';
import {
  BarChart3,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Loader2,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Database,
  Search,
  ThumbsUp,
  Layers
} from 'lucide-react';

function MemoryStatsCard({ data, isLoading, error, onRefresh }) {
  const [expanded, setExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('extraction');

  if (isLoading) {
    return (
      <div className="md-card md-card--loading">
        <div className="md-card-header">
          <BarChart3 size={18} />
          <span>Statistics</span>
        </div>
        <div className="md-card-loading">
          <Loader2 size={24} className="md-spin" />
          <span>Loading statistics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="md-card md-card--error">
        <div className="md-card-header">
          <BarChart3 size={18} />
          <span>Statistics</span>
          <button className="md-card-refresh" onClick={onRefresh}>
            <RefreshCw size={14} />
          </button>
        </div>
        <div className="md-card-error">
          <AlertTriangle size={20} />
          <span>Failed to load statistics</span>
          <code>{error.message}</code>
        </div>
      </div>
    );
  }

  const extraction = data?.extraction || {};
  const search = data?.search || {};
  const feedback = data?.feedback || {};
  const memory = data?.memory || {};

  const tabs = [
    { id: 'extraction', label: 'Extraction', icon: Database },
    { id: 'search', label: 'Search', icon: Search },
    { id: 'feedback', label: 'Feedback', icon: ThumbsUp },
    { id: 'distribution', label: 'Distribution', icon: Layers },
  ];

  return (
    <div className="md-card md-card--stats">
      <div
        className="md-card-header md-card-header--clickable"
        onClick={() => setExpanded(!expanded)}
      >
        <BarChart3 size={18} />
        <span>Statistics</span>
        <div className="md-card-header-right">
          <button className="md-card-refresh" onClick={(e) => { e.stopPropagation(); onRefresh(); }}>
            <RefreshCw size={14} />
          </button>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div className="md-card-content">
          {/* Tab Navigation */}
          <div className="md-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`md-tab ${activeTab === tab.id ? 'md-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={14} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="md-tab-content">
            {activeTab === 'extraction' && (
              <div className="md-metrics-grid">
                <MetricItem
                  label="Success Rate"
                  value={formatPercent(extraction.success_rate)}
                  trend={extraction.success_rate >= 0.95 ? 'up' : extraction.success_rate >= 0.90 ? 'flat' : 'down'}
                  target="≥95%"
                />
                <MetricItem
                  label="Avg Confidence"
                  value={formatPercent(extraction.avg_confidence)}
                  trend={extraction.avg_confidence >= 0.75 ? 'up' : extraction.avg_confidence >= 0.60 ? 'flat' : 'down'}
                  target="≥75%"
                />
                <MetricItem
                  label="Facts/Conversation"
                  value={formatNumber(extraction.avg_facts_per_conversation, 1)}
                  trend="flat"
                />
                <MetricItem
                  label="Fallback Rate"
                  value={formatPercent(extraction.fallback_rate)}
                  trend={extraction.fallback_rate <= 0.01 ? 'up' : extraction.fallback_rate <= 0.05 ? 'flat' : 'down'}
                  target="≤1%"
                  invertTrend
                />
                <MetricItem
                  label="Total Attempts"
                  value={formatNumber(extraction.total_attempts)}
                />
                <MetricItem
                  label="Failures"
                  value={formatNumber(extraction.failed_extractions || 0)}
                  trend={extraction.failed_extractions === 0 ? 'up' : 'down'}
                  invertTrend
                />
              </div>
            )}

            {activeTab === 'search' && (
              <div className="md-metrics-grid">
                <MetricItem
                  label="Hybrid Rate"
                  value={formatPercent(search.hybrid_rate)}
                  trend={search.hybrid_rate >= 0.90 ? 'up' : search.hybrid_rate >= 0.80 ? 'flat' : 'down'}
                  target="≥90%"
                />
                <MetricItem
                  label="Avg Results"
                  value={formatNumber(search.avg_results, 1)}
                  trend={search.avg_results >= 2 ? 'up' : search.avg_results >= 1 ? 'flat' : 'down'}
                  target="≥2.0"
                />
                <MetricItem
                  label="Combined Score"
                  value={formatPercent(search.avg_combined_score)}
                  trend={search.avg_combined_score >= 0.60 ? 'up' : search.avg_combined_score >= 0.50 ? 'flat' : 'down'}
                  target="≥60%"
                />
                <MetricItem
                  label="Total Searches"
                  value={formatNumber(search.total_searches)}
                />
                <MetricItem
                  label="Semantic Fallback"
                  value={formatPercent(search.semantic_fallback_rate)}
                  trend={search.semantic_fallback_rate <= 0.05 ? 'up' : search.semantic_fallback_rate <= 0.10 ? 'flat' : 'down'}
                  target="≤5%"
                  invertTrend
                />
                <MetricItem
                  label="Zero Results"
                  value={formatPercent(search.zero_result_rate)}
                  trend={search.zero_result_rate <= 0.10 ? 'up' : search.zero_result_rate <= 0.20 ? 'flat' : 'down'}
                  invertTrend
                />
              </div>
            )}

            {activeTab === 'feedback' && (
              <div className="md-metrics-grid">
                <MetricItem
                  label="Helpful Ratio"
                  value={formatPercent(feedback.helpful_ratio)}
                  trend={feedback.helpful_ratio >= 0.80 ? 'up' : feedback.helpful_ratio >= 0.60 ? 'flat' : 'down'}
                  target="≥80%"
                />
                <MetricItem
                  label="Helpful Count"
                  value={formatNumber(feedback.helpful_count)}
                  trend="up"
                />
                <MetricItem
                  label="Unhelpful Count"
                  value={formatNumber(feedback.unhelpful_count)}
                  trend={feedback.unhelpful_count === 0 ? 'up' : 'down'}
                  invertTrend
                />
                <MetricItem
                  label="Ignored Count"
                  value={formatNumber(feedback.ignored_count)}
                  trend="flat"
                />
                <MetricItem
                  label="Ignored Ratio"
                  value={formatPercent(feedback.ignored_ratio)}
                  trend={feedback.ignored_ratio <= 0.50 ? 'up' : feedback.ignored_ratio <= 0.70 ? 'flat' : 'down'}
                  target="≤50%"
                  invertTrend
                />
              </div>
            )}

            {activeTab === 'distribution' && (
              <div className="md-distribution">
                {/* By Tier */}
                <div className="md-distribution-section">
                  <h5 className="md-distribution-title">By Tier</h5>
                  <div className="md-distribution-bars">
                    <DistributionBar
                      label="Core"
                      value={memory.by_tier?.core || 0}
                      total={memory.total_memories || 1}
                      color="var(--color-primary)"
                    />
                    <DistributionBar
                      label="Recall"
                      value={memory.by_tier?.recall || 0}
                      total={memory.total_memories || 1}
                      color="var(--color-success)"
                    />
                    <DistributionBar
                      label="Archival"
                      value={memory.by_tier?.archival || 0}
                      total={memory.total_memories || 1}
                      color="var(--color-text-muted)"
                    />
                  </div>
                </div>

                {/* By Type */}
                <div className="md-distribution-section">
                  <h5 className="md-distribution-title">By Type (Top 5)</h5>
                  <div className="md-distribution-bars">
                    {Object.entries(memory.by_type || {})
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 5)
                      .map(([type, count]) => (
                        <DistributionBar
                          key={type}
                          label={type}
                          value={count}
                          total={memory.total_memories || 1}
                          color="var(--color-primary)"
                        />
                      ))}
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="md-metrics-grid md-metrics-grid--compact">
                  <MetricItem
                    label="Total Memories"
                    value={formatNumber(memory.total_memories)}
                  />
                  <MetricItem
                    label="Active Memories"
                    value={formatNumber(memory.active_memories)}
                  />
                  <MetricItem
                    label="Avg Health"
                    value={formatPercent(memory.avg_health_score)}
                    trend={memory.avg_health_score >= 0.50 ? 'up' : memory.avg_health_score >= 0.30 ? 'flat' : 'down'}
                  />
                  <MetricItem
                    label="Needs Review"
                    value={formatNumber(memory.needs_review_count)}
                    trend={memory.needs_review_count <= 50 ? 'up' : memory.needs_review_count <= 100 ? 'flat' : 'down'}
                    invertTrend
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricItem({ label, value, trend, target, invertTrend }) {
  const getTrendIcon = () => {
    if (!trend) return null;
    if (trend === 'up') return <TrendingUp size={12} className={invertTrend ? 'md-trend--down' : 'md-trend--up'} />;
    if (trend === 'down') return <TrendingDown size={12} className={invertTrend ? 'md-trend--up' : 'md-trend--down'} />;
    return <Minus size={12} className="md-trend--flat" />;
  };

  return (
    <div className="md-metric">
      <span className="md-metric-label">{label}</span>
      <div className="md-metric-value-row">
        <span className="md-metric-value">{value}</span>
        {getTrendIcon()}
      </div>
      {target && <span className="md-metric-target">Target: {target}</span>}
    </div>
  );
}

function DistributionBar({ label, value, total, color }) {
  const percent = total > 0 ? (value / total) * 100 : 0;

  return (
    <div className="md-bar-item">
      <div className="md-bar-header">
        <span className="md-bar-label">{label}</span>
        <span className="md-bar-value">{formatNumber(value)} ({percent.toFixed(1)}%)</span>
      </div>
      <div className="md-bar-track">
        <div
          className="md-bar-fill"
          style={{ width: `${percent}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function formatPercent(value) {
  if (value === undefined || value === null) return '-';
  return `${(value * 100).toFixed(1)}%`;
}

function formatNumber(value, decimals = 0) {
  if (value === undefined || value === null) return '-';
  if (typeof value !== 'number') return value;
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toFixed(decimals);
}

export default MemoryStatsCard;
